/** A category configuration is a NAMING/ROLE layer: it captions the hierarchy rungs and carries the rule sets inference consumes, never attribute VALUES — those are always live from `useStudioAttributesQuery`. Naming resolves at read time from the saved profile and nothing here writes or activates. */
import { POG_ATTRIBUTE_CANDIDATES, type PogAdapterOptions } from "../adapters/pogToStudioItems";
import type { AttributeCandidate } from "../domain/spineInference";
import { compileStudioProfile, type CompiledStudioItemMaster } from "./itemMasterWorkbook";
import type { StudioProfile, StudioSemanticRoles } from "./studioProfile";
import type { StudioAttributesData } from "./useStudioAttributesQuery";

/** The overlay attribute a `variants_restrictions` row matches: the backend matches `c.variant`, and `variant` IS CDT0, so the terminal-variant role must point here whatever the profile calls its terminal column. */
export const RESTRICTION_VARIANT_ATTR = "variant";

/** Profile attribute key → the live overlay attribute that level names. */
const overlayAttrOf = (profile: StudioProfile, key: string): string => {
  const attribute = profile.attributes.find((candidate) => candidate.key === key);
  return attribute?.overlayAttr ?? key;
};

/** Value-rule maps re-keyed into the live namespace: compiled rules are keyed by PROFILE attribute keys, inference looks them up by live column name, and an untranslated key silently matches nothing. */
const overlayValueRulesOf = (
  profile: StudioProfile,
  rules: Readonly<Record<string, readonly string[]>> | undefined,
): Record<string, readonly string[]> | undefined =>
  rules &&
  Object.fromEntries(
    Object.entries(rules).map(([key, values]) => [overlayAttrOf(profile, key), values]),
  );

/** Level captions for the strip, tree and findings. A live attribute the profile does not name keeps the overlay's own label: silence about a column is not a rename. */
export const namedOverlayCandidates = (
  profile: StudioProfile,
  candidates: readonly AttributeCandidate[],
): AttributeCandidate[] => {
  const labelByAttr = new Map(
    profile.attributes.map((attribute) => [overlayAttrOf(profile, attribute.key), attribute.label]),
  );
  return candidates.map((candidate) => ({
    ...candidate,
    label: labelByAttr.get(candidate.attr) ?? candidate.label,
  }));
};

/** The profile's semantic roles in the live namespace. A role naming a column the live data lacks is DROPPED, so `toResolverInputs` sees an unconfigured roles list and falls back to its spine-derived default rather than reading "nothing carries block identity". */
export const overlayRolesOf = (
  profile: StudioProfile,
  candidates: readonly AttributeCandidate[],
): StudioSemanticRoles => {
  const live = new Set(candidates.map((candidate) => candidate.attr));
  const translate = (key: string): string | undefined => {
    const attr = overlayAttrOf(profile, key);
    return live.has(attr) ? attr : undefined;
  };
  const translateList = (keys: readonly string[]): string[] =>
    keys.map(translate).filter((attr): attr is string => attr !== undefined);
  const roles = profile.semanticRoles;
  return {
    blockIdentity: translateList(roles.blockIdentity),
    vertical: translateList(roles.vertical),
    brand: translate(roles.brand) ?? roles.brand,
    tier: translate(roles.tier) ?? roles.tier,
    quantity: translate(roles.quantity) ?? roles.quantity,
    terminalVariant: live.has(RESTRICTION_VARIANT_ATTR)
      ? RESTRICTION_VARIANT_ATTR
      : (translate(roles.terminalVariant) ?? roles.terminalVariant),
  };
};

/** The studio's inputs for one category: LIVE attribute values, named and role-tagged by the active naming profile. */
export interface NamedStudioConfiguration extends CompiledStudioItemMaster {
  /** Always empty: values come from the live attributes API, never a stored workbook. */
  declaredValues: Readonly<Record<string, Readonly<Record<string, string | null>>>>;
  /** Live attribute → its configured caption, for naming the gap. */
  declaredLabels: Readonly<Record<string, string>>;
}

export const namedStudioConfigurationOf = (
  profile: StudioProfile,
  attributes: StudioAttributesData,
): NamedStudioConfiguration => {
  const compiled = compileStudioProfile(profile);
  const liveCandidates = attributes.adapterOptions.overlayCandidates ?? POG_ATTRIBUTE_CANDIDATES;
  const seen = new Set<string>();
  const fromProfile: AttributeCandidate[] = [];
  for (const attribute of profile.attributes) {
    const attr = overlayAttrOf(profile, attribute.key);
    if (seen.has(attr)) continue;
    seen.add(attr);
    fromProfile.push({ attr, label: attribute.label });
  }
  const candidates = namedOverlayCandidates(profile, [
    ...fromProfile,
    ...liveCandidates.filter((candidate) => !seen.has(candidate.attr)),
  ]);
  const preferredOrder = candidates.map((candidate) => candidate.attr);
  const semanticRoles = overlayRolesOf(profile, candidates);
  const normalization = attributes.adapterOptions.normalization;
  const adapterOptions: PogAdapterOptions = {
    ...attributes.adapterOptions,
    overlayCandidates: candidates,
    // Resolved per item by the adapter, so the candidates above are semantic names by the time inference runs.
    sectionSemantics: profile.sectionSemantics,
    sectionAttr: RESTRICTION_VARIANT_ATTR,
    // The only normalization knob the naming layer owns; an explicit `false` stands.
    normalization:
      normalization === false
        ? false
        : { ...normalization, placeholderSpellings: profile.missingValues },
  };
  return {
    ...compiled,
    adapterOptions,
    pogAdapterOptions: adapterOptions,
    preferredOrder,
    inferenceOptions: {
      ...compiled.inferenceOptions,
      preferredOrder,
      // CDT0 is the root by construction, not by measurement; asserted only when the live data carries the column.
      ...(candidates.some((candidate) => candidate.attr === RESTRICTION_VARIANT_ATTR)
        ? { rootAttr: RESTRICTION_VARIANT_ATTR }
        : {}),
      firstPreferredValues: overlayValueRulesOf(
        profile,
        compiled.inferenceOptions.firstPreferredValues,
      ),
      lastPreferredValues: overlayValueRulesOf(
        profile,
        compiled.inferenceOptions.lastPreferredValues,
      ),
    },
    aestheticsRules: {
      ...compiled.aestheticsRules,
      brandAttr: semanticRoles.brand,
      tierAttr: semanticRoles.tier,
      sizeAttr: semanticRoles.quantity,
      flavourAttr: semanticRoles.terminalVariant,
      verticalAttrs: semanticRoles.vertical,
    },
    semanticRoles,
    declaredValues: {},
    declaredLabels: Object.fromEntries(
      profile.attributes.map((attribute) => [
        overlayAttrOf(profile, attribute.key),
        attribute.label,
      ]),
    ),
  };
};
